// The delivery plan: what a signed SOW turns into.
//
// Written on the quote (and on templates) as milestones, each due a
// number of days after kickoff, each with the tasks that make it up. On
// acceptance it becomes dated project milestones with their tasks as
// subtasks - so the technician has a checklist and the client a progress
// bar without anyone retyping the plan.
//
// Days AFTER KICKOFF, not dates: a quote is written before anyone knows
// when it will be signed, and a plan pinned to calendar dates would be
// wrong the day it went out. Kickoff is the day the client accepts.
//
// The client reads it on the quote, as a "Delivery plan" section, so it
// is part of the frozen snapshot and of what they sign (2026-10-05).
/** Two years. Anything longer is a typo, not a plan. */
export const MAX_DELIVERY_DAYS = 730;
/**
 * `YYYY-MM-DD` plus whole days, as a `YYYY-MM-DD`.
 *
 * String in, string out, through UTC: `new Date('2026-10-05')` is
 * midnight UTC, which is the previous evening in New York, and doing the
 * arithmetic in local time is how a due date slips a day.
 */
export function addDaysIso(dateIso, days) {
    const [y, m, d] = dateIso.split('-').map(Number);
    const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
    return new Date(t).toISOString().slice(0, 10);
}
/**
 * When a milestone falls, in words a client reads: "At kickoff",
 * "2 weeks after signing", "10 days after signing". Whole weeks are said
 * as weeks because that is how a timeline is read; anything else is days,
 * never rounded, because "about two weeks" is not something to sign.
 */
export function describeDueAfter(days) {
    if (days <= 0)
        return 'At kickoff';
    if (days % 7 === 0) {
        const weeks = days / 7;
        return `${weeks} week${weeks === 1 ? '' : 's'} after signing`;
    }
    return `${days} day${days === 1 ? '' : 's'} after signing`;
}
/** The latest milestone in a plan, as days after kickoff; null when empty. */
export function planLengthDays(plan) {
    if (plan.length === 0)
        return null;
    return Math.max(...plan.map((m) => m.dueAfterDays));
}
/**
 * How far along a project is, 0-100.
 *
 * ONE function, used by the technician's panel and the client's bar, so
 * the two can never show different numbers for the same project.
 *
 * Tasks are the measure: they are what gets checked off. Rounded DOWN,
 * so 100% means every task is done - "100%" with one box still empty is
 * the thing a client would rightly ring up about. A project marked
 * completed is 100 whatever its list says; one with no tasks falls back
 * to its milestones, and with neither it is 0.
 */
export function projectProgressPercent(args) {
    if (args.status === 'completed')
        return 100;
    if (args.tasksTotal > 0)
        return Math.floor((args.tasksDone / args.tasksTotal) * 100);
    const mTotal = args.milestonesTotal ?? 0;
    if (mTotal > 0)
        return Math.floor(((args.milestonesDone ?? 0) / mTotal) * 100);
    return 0;
}
//# sourceMappingURL=delivery.js.map